/**
 * The WSA-Enlil frame and player on the Space weather page (decision 690; spec §4.3 F, §5.2; MB 27 Oct 2026: fast).
 * The latest frame shows; "Play" loads the animation only when pressed, from Teslyn Cloud's own copy of the run
 * (Sydney) when it holds the run complete, else from SWPC's Enlil folder (`enlilFramesToLoad`), six frames at a time
 * in order, and starts playing as soon as the first frames are in, the rest loading behind it with a progress bar. A
 * phone plays every second frame (about half the data), each shown twice as long. The Diurnal tool's controls: first,
 * previous, play or pause, next, last, a slider; Space, ← and → with the player focused.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  ENLIL_START_AFTER,
  dayAndUtc,
  enlilFrameUrl,
  enlilFramesToLoad,
  enlilPlayOrder,
  loadInOrder,
  type EnlilFrame,
  type EnlilLoadFrame,
  type EnlilManifest,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { useMediaQuery } from '../components/useMediaQuery.ts';

export const ENLIL_FRAME_MS = 150;
const FRAME_KB = 95;
/** A phone: every second frame. */
export const PHONE_QUERY = '(max-width: 599.98px)';

/** Where frames come from: Teslyn Cloud's copy (its manifest and frames) and SWPC; tests give their own. */
export interface EnlilSource {
  /** The copy's manifest, or null when there is none (or it cannot be read). */
  manifest(): Promise<EnlilManifest | null>;
  /** A frame's picture address, once it is loaded: a copy frame as a `blob:` address, else SWPC's own. */
  frameUrl(f: EnlilLoadFrame): Promise<string>;
  release(url: string): void;
}

/** SWPC's own address of a frame, loaded through an image (no referrer). */
export function swpcFrameUrl(f: Pick<EnlilLoadFrame, 'path'>): Promise<string> {
  const url = enlilFrameUrl(f);
  if (!url) return Promise.reject(new Error('Not an Enlil frame.'));
  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.referrerPolicy = 'no-referrer';
    img.onload = () => resolve(url);
    img.onerror = () => reject(new Error('A frame could not be loaded from NOAA.'));
    img.src = url;
  });
}

export function EnlilPlayer({
  frames,
  latest,
  source,
}: {
  readonly frames: readonly EnlilFrame[];
  readonly latest: EnlilFrame | null;
  readonly source: EnlilSource;
}): ReactElement {
  const phone = useMediaQuery(PHONE_QUERY);
  const [list, setList] = useState<readonly EnlilLoadFrame[] | null>(null);
  const [urls, setUrls] = useState<readonly string[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [fromCopy, setFromCopy] = useState(false);
  const stopped = useRef(false);
  const made = useRef<string[]>([]);
  useEffect(
    () => () => {
      stopped.current = true;
      for (const u of made.current) source.release(u);
    },
    [source],
  );
  const order = list ? enlilPlayOrder(list.length, phone) : [];
  const frameMs = phone ? ENLIL_FRAME_MS * 2 : ENLIL_FRAME_MS;

  const play = useCallback(async (): Promise<void> => {
    if (list) {
      setPlaying(true);
      return;
    }
    setError(null);
    setLoading(true);
    const manifest = await source.manifest().catch(() => null);
    const toLoad = enlilFramesToLoad(frames, manifest);
    const idx = enlilPlayOrder(toLoad.length, phone);
    setFromCopy(Boolean(manifest));
    setList(toLoad);
    setTotal(idx.length);
    const got: string[] = [];
    const r = await loadInOrder({
      count: idx.length,
      load: (k) => source.frameUrl(toLoad[idx[k]!]!),
      onLoaded: (_k, url) => {
        if (url.startsWith('blob:')) made.current.push(url);
        got.push(url);
        setUrls([...got]);
        if (got.length === Math.min(ENLIL_START_AFTER, idx.length)) setPlaying(true);
      },
      stopped: () => stopped.current,
    });
    if (stopped.current) return;
    setLoading(false);
    if (r.failed > 0)
      setError(
        `${r.failed} of ${idx.length} frames could not be loaded; the animation plays the ${got.length} that did.`,
      );
  }, [frames, list, phone, source]);

  // the animation runs over the frames loaded so far (in order from the first), then all of them
  const n = urls.length;
  useEffect(() => {
    if (!playing || n === 0) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % n), frameMs);
    return () => clearInterval(t);
  }, [playing, n, frameMs]);

  // the latest frame straight from SWPC's address (one picture; the copy is for the whole animation)
  const still = latest ? enlilFrameUrl(latest) : null;

  const step = (d: number): void => {
    if (!n) return;
    setPlaying(false);
    setI((v) => (v + d + n) % n);
  };
  const shownIndex = n ? order[Math.min(i, n - 1)] : undefined;
  const frame = list && shownIndex !== undefined ? list[shownIndex] : latest;
  const src = n ? urls[Math.min(i, n - 1)] : still;
  const sizeMb = (((phone ? Math.ceil(frames.length / 2) : frames.length) * FRAME_KB) / 1024).toFixed(0);

  return (
    <div
      className="stack sw-enlil"
      tabIndex={n ? 0 : -1}
      aria-label="WSA-Enlil"
      onKeyDown={(e) => {
        if (!n) return;
        if (e.key === ' ') {
          e.preventDefault();
          setPlaying((p) => !p);
        } else if (e.key === 'ArrowLeft') step(-1);
        else if (e.key === 'ArrowRight') step(1);
      }}
    >
      {src ? (
        <img
          className="sw-enlil__img"
          src={src}
          referrerPolicy="no-referrer"
          alt={`WSA-Enlil solar wind model, ${frame?.time ? dayAndUtc(frame.time) : 'latest frame'}`}
        />
      ) : (
        <p className="caption muted">No Enlil frame list yet.</p>
      )}
      <p className="caption muted">
        {frame?.time ? `Frame ${dayAndUtc(frame.time)}` : ''}
        {n ? ` · ${Math.min(i, n - 1) + 1} of ${total}` : ''} · Source: NOAA SWPC (WSA-Enlil model)
        {fromCopy ? ', from Teslyn Cloud’s copy' : ''}
      </p>
      {loading && (
        <div className="sw-enlil__progress" role="status">
          <progress max={total || 1} value={n} aria-label="Frames loaded" />
          <span className="caption muted">
            {n} of {total} frames loaded
            {n >= Math.min(ENLIL_START_AFTER, total) && n > 0 ? '; playing as they come' : ''}
          </span>
        </div>
      )}
      {list ? (
        <div className="row sw-enlil__controls" role="group" aria-label="Enlil player">
          <Button
            variant="quiet"
            aria-label="First frame"
            onClick={() => {
              setPlaying(false);
              setI(0);
            }}
          >
            First
          </Button>
          <Button variant="quiet" aria-label="Previous frame" onClick={() => step(-1)}>
            Previous
          </Button>
          <Button disabled={!n} onClick={() => setPlaying((p) => !p)}>
            {playing ? 'Pause' : 'Play'}
          </Button>
          <Button variant="quiet" aria-label="Next frame" onClick={() => step(1)}>
            Next
          </Button>
          <Button
            variant="quiet"
            aria-label="Last frame"
            onClick={() => {
              setPlaying(false);
              setI(Math.max(0, n - 1));
            }}
          >
            Last
          </Button>
          <input
            className="sw-enlil__slider"
            type="range"
            min={0}
            max={Math.max(0, n - 1)}
            value={Math.min(i, Math.max(0, n - 1))}
            aria-label="Frame"
            onChange={(e) => {
              setPlaying(false);
              setI(Number(e.target.value));
            }}
          />
        </div>
      ) : (
        <div className="stack">
          <Button disabled={!frames.length} onClick={() => void play()}>
            {frames.length
              ? `Play (${phone ? Math.ceil(frames.length / 2) : frames.length} frames, about ${sizeMb} MB)`
              : 'Play'}
          </Button>
          {frames.length > 0 && (
            <span className="caption muted">
              Only when pressed; it starts as soon as the first frames arrive.
              {phone ? ' On a phone every second frame is played.' : ''}
            </span>
          )}
        </div>
      )}
      {error && (
        <p className="caption notice notice--fail" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
